import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { organizationApi, Organization, OrganizationRQ } from '../../utils/organizationApi';
import { useAuth } from '../../contexts/AuthContext';
import CreateOrganizationWithUserForm from './CreateOrganizationWithUserForm';
import WhiteLabelCreditInfoComponent from '../WhiteLabelCreditInfo';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Fab,
  useTheme,
  useMediaQuery,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import DataTable, { Column } from './DataTable';

function OrganizationsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filtered, setFiltered] = useState<Organization[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const rs = await organizationApi.list();
      setOrganizations(rs);
      setFiltered(rs);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar organizações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const txt = searchText.trim().toLowerCase();
    if (!txt) { setFiltered(organizations); return; }
    setFiltered(
      organizations.filter(o =>
        o.name?.toLowerCase().includes(txt) ||
        o.identifier?.toLowerCase().includes(txt)
      )
    );
  }, [searchText, organizations]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a as any)[orderBy] ?? '';
      const bv = (b as any)[orderBy] ?? '';
      if (typeof av === 'string' && typeof bv === 'string') {
        return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return 0;
    });
    return arr;
  }, [filtered, orderBy, order]);

  const paged = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja remover esta organização?')) return;
    try {
      await organizationApi.remove(id);
      toast.success('Organização removida');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao remover');
    }
  };

  const handleOpenActionsModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    setActionsModalOpen(true);
  };

  const handleCloseActionsModal = () => {
    setActionsModalOpen(false);
    setSelectedOrganization(null);
  };

  const handleEditFromModal = () => {
    if (selectedOrganization) {
      navigate(`${selectedOrganization.id}/edit`);
      handleCloseActionsModal();
    }
  };

  const handleDeleteFromModal = () => {
    if (selectedOrganization) {
      handleCloseActionsModal();
      handleDelete(selectedOrganization.id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const columns: Column<Organization>[] = isMobile ? [
    { id: 'name', label: 'Nome', minWidth: 120 },
    {
      id: 'credit',
      label: 'Créditos',
      minWidth: 80,
      format: (value) => value?.toLocaleString('pt-BR') || '0'
    },
    {
      id: 'endIn',
      label: 'Vencimento',
      minWidth: 100,
      format: (value) => value ? formatDate(value) : 'Sem vencimento'
    },
    {
      id: 'active',
      label: 'Status',
      minWidth: 80,
      format: (value) => (
        <Chip
          label={value ? 'Ativo' : 'Inativo'}
          sx={{ 
            color: theme.palette.mode === 'dark' ? '#000' : undefined,
            fontSize: '0.75rem',
            height: '24px'
          }}
          size="small"
        />
      )
    },
    {
      id: 'actions',
      label: 'Ações',
      minWidth: 60,
      format: (_v, row) => (
        <IconButton
          size="small"
          onClick={() => handleOpenActionsModal(row)}
          sx={{ padding: '4px' }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )
    }
  ] : [
    { id: 'name', label: 'Nome', minWidth: 200 },
    { id: 'identifier', label: 'Identificador', minWidth: 180 },
    {
      id: 'credit',
      label: 'Créditos',
      minWidth: 120,
      format: (value) => value?.toLocaleString('pt-BR') || '0'
    },
    {
      id: 'endIn',
      label: 'Vencimento',
      minWidth: 140,
      format: (value) => value ? formatDate(value) : 'Sem vencimento'
    },
    {
      id: 'active',
      label: 'Status',
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value ? 'Ativo' : 'Inativo'}
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          size="small"
        />
      )
    },
    {
      id: 'actions',
      label: 'Ações',
      minWidth: 120,
      format: (_v, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-center' }}>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => navigate(`${row.id}/edit`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={() => handleDelete(row.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100vw',
      overflow: 'hidden',
      px: 0,
      mx: 0
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: isMobile ? 'center' : 'space-between', 
        alignItems: 'center', 
        mb: isMobile ? 1 : 2, 
        gap: 1, 
        flexDirection: { xs: 'column', sm: 'row' },
        textAlign: isMobile ? 'center' : 'left',
        px: isMobile ? 1 : 0
      }}>
        <Typography 
          variant={isMobile ? "h6" : "h5"}
          sx={{ 
            fontWeight: 'bold',
            textAlign: 'center',
            width: isMobile ? '100%' : 'auto',
            fontSize: isMobile ? '1.1rem' : '1.5rem'
          }}
        >
          Gerenciar Organizações
        </Typography>
        {!isMobile && (
          <Button variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} startIcon={<AddIcon />} onClick={() => navigate('create-with-user')}>
            Nova Organização
          </Button>
        )}
      </Box>

      {user?.isWhiteLabel && user.whiteLabelCreditInfo && (
        <Box sx={{ mb: isMobile ? 1 : 2, px: isMobile ? 1 : 0 }}>
          <WhiteLabelCreditInfoComponent creditInfo={user.whiteLabelCreditInfo} />
        </Box>
      )}

      <Paper sx={{ 
        p: isMobile ? 1 : 1.5, 
        mb: isMobile ? 1 : 2,
        mx: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <TextField
          fullWidth
          placeholder="Buscar por nome ou identificador"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{ 
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize={isMobile ? "small" : "medium"} />
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: isMobile ? '13px' : '14px',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {filtered.length} {filtered.length === 1 ? 'organização encontrada' : 'organizações encontradas'}
        </Typography>
      </Paper>

      <Box sx={{ mb: isMobile ? 0 : 2 }}>
        <DataTable<Organization>
          columns={columns}
          rows={paged}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          total={sorted.length}
          orderBy={orderBy}
          order={order}
          onPageChange={(p) => setPage(p)}
          onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
          onRequestSort={(col) => {
            if (orderBy === col) {
              setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
              setOrderBy(col);
              setOrder('asc');
            }
          }}
        />
      </Box>

      {/* Modal de Ações para Mobile */}
      <Dialog 
        open={actionsModalOpen} 
        onClose={handleCloseActionsModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Ações - {selectedOrganization?.name}
        </DialogTitle>
        <DialogContent>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleEditFromModal}>
                <ListItemIcon>
                  <EditIcon />
                </ListItemIcon>
                <ListItemText primary="Editar Organização" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleDeleteFromModal}>
                <ListItemIcon>
                  <DeleteIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="Excluir Organização" />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionsModal}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {isMobile && (
        <Fab 
          color="primary" 
          onClick={() => navigate('create-with-user')} 
          sx={{ 
            position: 'fixed', 
            bottom: 16, 
            right: 16,
            zIndex: 1000
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
}

function OrganizationForm({ isEdit }: { isEdit?: boolean }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const { user } = useAuth();
  const organizationId = params.organizationId as string | undefined;
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!isEdit);
  const [errors, setErrors] = useState<{ credit?: string }>({});
  const [form, setForm] = useState<OrganizationRQ>({
    name: '',
    identifier: '',
    credit: 10000,
    currentPlan: undefined,
    endIn: undefined,
    permissions: organizationApi.getDefaultPermissions()
  });

  useEffect(() => {
    const load = async () => {
      if (!isEdit || !organizationId) { setInitialLoading(false); return; }
      try {
        const rs = await organizationApi.get(organizationId);
        setForm({
          name: rs.name || '',
          identifier: rs.identifier || '',
          credit: rs.credit || 10000,
          currentPlan: rs.currentPlan,
          endIn: rs.endIn,
          permissions: rs.permissions || organizationApi.getDefaultPermissions()
        });
      } catch (e: any) {
        toast.error(e?.message || 'Erro ao carregar organização');
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [isEdit, organizationId]);

  const canSubmit = useMemo(() => {
    const basicValidation = form.name && form.identifier;

    // Validação de créditos será feita pelo backend

    return basicValidation;
  }, [form, user, isEdit]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEdit && organizationId) {
        const result = await organizationApi.update(organizationId, form);
        toast.success('Organização atualizada');
        navigate('..');
      } else {
        const result = await organizationApi.create(form);
        toast.success('Organização criada');
        navigate('..');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao salvar organização');
    } finally {
      setLoading(false);
    }
  };

  const updatePermissions = (path: string, value: boolean) => {
    setForm(prev => {
      const newPermissions = { ...prev.permissions };
      const pathParts = path.split('.');
      let current: any = newPermissions;

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;

      return { ...prev, permissions: newPermissions };
    });
  };

  if (initialLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('..')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {isEdit ? 'Editar Organização' : 'Nova Organização'}
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Informações Básicas
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <TextField
              fullWidth
              label="Nome"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Identificador"
              value={form.identifier}
              onChange={e => setForm({ ...form, identifier: e.target.value })}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <TextField
              fullWidth
              type="number"
              label="Créditos"
              value={form.credit}
              onChange={e => setForm({ ...form, credit: parseInt(e.target.value) || 0 })}
              helperText={
                user?.isWhiteLabel && user.whiteLabelCreditInfo
                  ? `Disponível: ${user.whiteLabelCreditInfo.availableCredits.toLocaleString()}/${user.whiteLabelCreditInfo.maxCredits.toLocaleString()}`
                  : undefined
              }
              error={!!errors.credit}
              FormHelperTextProps={{
                error: !!errors.credit
              }}
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Data de Vencimento"
              value={form.endIn ? new Date(form.endIn).toISOString().slice(0, 16) : ''}
              onChange={e => setForm({ ...form, endIn: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </Box>

      {/* Permissões de Menu - Oculto para usuários whitelabel */}
      {!user?.isWhiteLabel && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MenuIcon />
              <Typography variant="h6">Permissões de Menu</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {Object.entries(form.permissions.menuPermissions).map(([key, value]) => (
                <Box key={key} sx={{ minWidth: { xs: '100%', sm: '45%', md: '30%' } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(_, checked) => updatePermissions(`menuPermissions.${key}`, checked)}
                      />
                    }
                    label={getMenuLabel(key)}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Permissões de Alertas - Oculto para usuários whitelabel */}
      {!user?.isWhiteLabel && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon />
              <Typography variant="h6">Permissões de Alertas</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {Object.entries(form.permissions.alertPermissions).map(([key, value]) => (
                <Box key={key} sx={{ minWidth: { xs: '100%', sm: '45%' } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(_, checked) => updatePermissions(`alertPermissions.${key}`, checked)}
                      />
                    }
                    label={getAlertLabel(key)}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Outras Permissões */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6">Outras Permissões</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={form.permissions.canCreateAgents}
                onChange={(_, checked) => updatePermissions('canCreateAgents', checked)}
              />
            }
            label="Pode criar novos agentes"
          />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={() => navigate('..')}>Cancelar</Button>
        <Button variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} disabled={!canSubmit || loading} onClick={handleSubmit}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </Box>
    </Paper>
  );
}

function getMenuLabel(key: string): string {
  const labels: Record<string, string> = {
    canViewDashboard: 'Dashboard',
    canViewLeads: 'Leads',
    canViewConversations: 'Conversas',
    canViewReports: 'Relatórios',
    canViewAgents: 'Agentes',
    canViewTemplates: 'Templates',
    canViewCredits: 'Créditos',
    canViewPlans: 'Planos',
    canViewUsers: 'Usuários',
    canViewSegments: 'Segmentos',
    canViewSettings: 'Configurações',
    canViewFinanceiro: 'Financeiro',
  };
  return labels[key] || key;
}

function getAlertLabel(key: string): string {
  const labels: Record<string, string> = {
    showTrialAlerts: 'Alertas de período de teste',
    showPaymentAlerts: 'Alertas de pagamento',
    showWelcomeModals: 'Modais de boas-vindas',
    showPlanInactiveAlerts: 'Alertas de plano inativo',
  };
  return labels[key] || key;
}

const OrganizationsManagement: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<OrganizationsList />} />
      <Route path="/create-with-user" element={<CreateOrganizationWithUserForm />} />
      <Route path=":organizationId/edit" element={<OrganizationForm isEdit />} />
    </Routes>
  );
};

export default OrganizationsManagement;
